import React, { useState, useEffect, useContext, useCallback } from 'react';
import { useParams, Link, Navigate, useNavigate } from 'react-router-dom';
import { Shield, Key, FileWarning, Settings, Bell, Clock, ArrowLeft, Check, Globe, HashIcon } from 'lucide-react';
import { 
  Notification, 
  NotificationDetail as ApiNotificationDetail, 
  NotificationsApi, 
  NotificationDetail,
  PaginatedDomainList,
  PaginatedDomainListFromJSON,
  PaginatedSubdomainList,
  PaginatedSubdomainListFromJSON,
  PaginatedIPAddressList,
  PaginatedIPAddressListFromJSON,
  PaginatedPortList,
  PaginatedPortListFromJSON,
  PaginatedFindingList,
  PaginatedFindingListFromJSON,
  PaginatedPublicLeakList,
  PaginatedPublicLeakListFromJSON,
  PaginatedStealerLeakList,
  PaginatedStealerLeakListFromJSON,
  PaginatedDataLeakList,
  PaginatedDataLeakListFromJSON,
  Domain
} from 'api-client';
import { getApiConfiguration } from '@/api/utils';
import { useTeams } from '@/contexts/TeamsContext';
import { AuthContext } from '@/contexts/AuthContext';
import { DomainsTable } from '@/components/assets/DomainsTable';
import { SubdomainsTable } from '@/components/assets/SubdomainsTable';
import { IpAddressesTable } from '@/components/assets/IpAddressesTable';
import { PortsTable } from '@/components/assets/PortsTable';
import { FindingsTable } from '@/components/findings/FindingsTable';
import PublicLeaksList from './data-protection/PublicLeaksList';
import StealerLeaksList from './data-protection/StealerLeaksList';
import DataLeaksList from './data-protection/DataLeaksList';

// Helper function to format timestamp for display
const formatTimestamp = (timestamp: Date | string | undefined): string => {
  if (!timestamp) return '';
  const date = timestamp instanceof Date ? timestamp : new Date(timestamp);
  return new Intl.DateTimeFormat('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(date);
};

/**
 * Generic Notification Detail component that handles different notification types
 * based on the type parameter from the URL
 */
const GenericNotificationDetail: React.FC = () => {
  // Get both type and id from URL parameters
  const { type, id } = useParams<{ type: string; id: string }>();
  const navigate = useNavigate();
  const { currentTeam } = useTeams();
  const { token } = useContext(AuthContext);
  const client = new NotificationsApi(getApiConfiguration(token));
  const [notification, setNotification] = useState<NotificationDetail | null>(null);
  const [parsedRelatedObjects, setParsedRelatedObjects] = useState<any>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = React.useState(1);
  const [paginationInfo, setPaginationInfo] = React.useState<{
    count: number;
    next: string | null;
    previous: string | null;
  }>({ count: 0, next: null, previous: null });
  const [selectedFindings, setSelectedFindings] = useState<string[]>([]);
  const [findings, setFindings] = useState<any[]>([]);
  const [assignees, setAssignees] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  
  // Add state for all notifications
  const [allNotifications, setAllNotifications] = useState<Notification[]>([]);
  const [sidebarLoading, setSidebarLoading] = useState<boolean>(true);

  const handleSelectAll = (selected: boolean) => {
    setSelectedFindings(selected ? findings.map(f => f.id) : []);
  };

  const handleSelectFinding = (id: string, selected: boolean) => {
    if (selected) {
      setSelectedFindings(prev => [...prev, id]);
    } else {
      setSelectedFindings(prev => prev.filter(fid => fid !== id));
    }
  };

  const handleAssignFinding = (findingId: string, assigneeId: string) => {
    console.log('Assigning finding', findingId, 'to assignee', assigneeId);
  };

  const handleFindingAction = (findingId: string, action: string) => {
    console.log('Performing action', action, 'on finding', findingId);
  };

  /**
   * Parse related objects based on notification type
   * @param relatedObjectsJson - JSON string of related objects
   * @param objectType - Type of the notification object
   * @returns Parsed related objects
   */
  const parseRelatedObjects = (relatedObjectsJson: any, objectType: string) => {
    try {
      console.log(relatedObjectsJson, objectType)
      // If relatedObjectsJson is not a string or is empty, return null
      if (!relatedObjectsJson) {
        return null;
      }

      // Parse the JSON string
      const parsedJson = relatedObjectsJson;

      // Based on the object type, use the appropriate FromJSON function
      switch (objectType) {
        case 'Domain':
          return PaginatedDomainListFromJSON(parsedJson);
        case 'Subdomain':
          return PaginatedSubdomainListFromJSON(parsedJson);
        case 'Ip_Address':
          return PaginatedIPAddressListFromJSON(parsedJson);
        case 'Ports':
          return PaginatedPortListFromJSON(parsedJson);
        case 'Finding':
          return PaginatedFindingListFromJSON(parsedJson);
        case 'Publicleak':
          return PaginatedPublicLeakListFromJSON(parsedJson);
        case 'Stealerleak':
          return PaginatedStealerLeakListFromJSON(parsedJson);
        case 'Dataleak':
          return PaginatedDataLeakListFromJSON(parsedJson);
        default:
          // For unknown types, just return the parsed JSON
          return parsedJson;
      }
    } catch (error) {
      console.error('Error parsing related objects:', error);
      return null;
    }
  };

  const fetchNotificationDetail = async () => {
    if (!id || !currentTeam?.slug) return;
    
    setLoading(true);
    try {
      // Pass the notification type as a parameter to the API call if needed
      const response = await client.notificationsNotificationsRetrieve({ 
        id, 
        teamSlug: currentTeam?.slug,
        // You might need to add a type parameter to your API call if your backend supports it
        // type: type
      });

      setNotification(response);

      // Parse related objects based on the notification type
      if (response.relatedObjects) {
        const parsed = parseRelatedObjects(response.relatedObjects, response.objectType);
        setParsedRelatedObjects(parsed);
        setPaginationInfo({
          count: parsed.count,
          next: parsed.next,
          previous: parsed.previous
        });
      }
    } catch (error) {
      console.error('Error fetching notification details:', error);
      setError('Failed to load notification details. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  // Add function to fetch all notifications for the sidebar
  const fetchAllNotifications = useCallback(async () => {
    if (!currentTeam?.slug) return;
    
    setSidebarLoading(true);
    try {
      const response = await client.notificationsNotificationsList({ 
        teamSlug: currentTeam?.slug,
      });
      
      setAllNotifications(response.results);
    } catch (error) {
      console.error('Error fetching notifications for sidebar:', error);
    } finally {
      setSidebarLoading(false);
    }
  }, [currentTeam?.slug]);

  const handleReadNotification = async (notifId: number, notif: Notification) => {
    if (!token || !currentTeam?.slug || !notifId) return;

    const response = await client.notificationsNotificationsUpdate({ teamSlug: currentTeam?.slug, id: notifId.toString(), notification: { ...notif, read: true } });
    // setAllNotifications(prev => prev.map(n => n.id === notifId ? { ...n, read: true } : n))
    // setNotification({...notification, read: true});
  };

  useEffect(() => {
    fetchNotificationDetail();
    fetchAllNotifications();
  }, [id, currentTeam?.slug, token, fetchAllNotifications]);

  // Helper function to format timestamp for display in sidebar
  const formatTimestamp = (timestamp: Date | string | undefined): string => {
    if (!timestamp) return '';
    const date = timestamp instanceof Date ? timestamp : new Date(timestamp);
    return new Intl.DateTimeFormat('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    }).format(date);
  };

  // Helper function to get notification icon
  const getNotificationIcon = (notification: Notification) => {
    switch (notification.objectType) {
      case 'Domain': return Globe;
      case 'Subdomain': return Globe;
      case 'Ip_Address': return Globe;
      case 'Ports': return Globe;
      case "Finding": return Shield;
      case "Publicleak": return Key;
      case "Stealerleak": return Key;
      case "Dataleak": return HashIcon;
      default: return Bell;
    }
  };

  // Helper function to get notification color
  const getNotificationColor = (notification: Notification) => {
    switch (notification.severity) {
      case 'critical': return 'text-red-500 bg-red-500/10';
      case 'high': return 'text-red-500 bg-red-500/10';
      case 'info': return 'text-blue-500 bg-blue-500/10';
      case 'medium': return 'text-purple-500 bg-purple-500/10';
      case 'low': return 'text-emerald-500 bg-emerald-500/10';
      default: return 'text-blue-500 bg-blue-500/10';
    }
  };

  const renderDomainContent = () => {
    if (!parsedRelatedObjects) return <div>No domain information available</div>;

    const transformedDomains = parsedRelatedObjects.results.map((domain: any) => ({
      id: domain.id.toString(),
      name: domain.domainName || 'Unknown',
      type: 'web',
      status: domain.isActive ? 'active' : 'inactive',
      riskScore: (domain.riskScore || 'n/a').toUpperCase(),
      updatedAt: new Date(domain.updatedAt).toLocaleString(),
      tags: domain.tags || [],
      vulnerabilities: {
        critical: domain.findingsStats.critical,
        high: domain.findingsStats.high,
        medium: domain.findingsStats.medium,
        low: domain.findingsStats.low
      },
      technologies: domain.technologies?.map((tech: any) => ({
        name: tech.name || 'Unknown',
        version: tech.version || 'Unknown',
        logo_path: tech.logo_path || '',
        color: tech.color || 'gray'
      })) || [],
      registrar: domain.registrar,
      registrationDate: domain.registrationDate,
      expirationDate: domain.expirationDate,
      tlsCertificate: domain.tlsCertificate ? {
        sni: domain.tlsCertificate.sni,
        host: domain.tlsCertificate.host,
        port: domain.tlsCertificate.port,
        cipher: domain.tlsCertificate.cipher,
        issuer_cn: domain.tlsCertificate.issuer_cn,
        not_after: domain.tlsCertificate.not_after,
        not_before: domain.tlsCertificate.not_before,
        subject_cn: domain.tlsCertificate.subject_cn,
        tls_version: domain.tlsCertificate.tls_version,
        probe_status: domain.tlsCertificate.probe_status
      } : null,
      subdomains: {
        total: domain.subdomainsCount,
        active: Math.floor(Math.random() * 15) + 3, // Random number between 3 and 18
        inactive: Math.floor(Math.random() * 7) + 2 // Random number between 2 and 9
      }
    }));
    
    return (
      <div className="space-y-6">
        <h2 className="text-2xl font-bold text-white">Domain Notification</h2>
        {transformedDomains && transformedDomains.length > 0 ? (
          <DomainsTable
            domains={transformedDomains}
            currentPage={currentPage}
            onPageChange={setCurrentPage}
            totalDomains={paginationInfo.count}
            itemsPerPage={10}
            paginationInfo={paginationInfo}
          />
        ) : (
          <div className="text-gray-300">No domains found</div>
        )}
      </div>
    );
  };

  const renderSubdomainContent = () => {
    const transformedSubdomains = parsedRelatedObjects.results.map((subdomain: any) => ({
      id: subdomain.id.toString(),
      subdomainName: subdomain.subdomainName,
      parentDomain: {
        id: subdomain.parentDomain.id.toString(),
        name: subdomain.parentDomain.domainName || 'Unknown'
      },
      isActive: subdomain.isActive || false,
      riskScore: (subdomain.riskScore || 'n/a').toUpperCase(),
      updatedAt: new Date(subdomain.updatedAt).toLocaleString(),
      vulnerabilities: {
        critical: subdomain.findingsStats?.critical || 0,
        high: subdomain.findingsStats?.high || 0,
        medium: subdomain.findingsStats?.medium || 0,
        low: subdomain.findingsStats?.low || 0
      },
      createdAt: new Date(subdomain.createdAt).toLocaleString(),
      tags: subdomain.tags || [],
      technologies: subdomain.technologies?.map((tech: any) => ({
        name: tech.name || 'Unknown',
        version: tech.version || 'Unknown',
        logo_path: tech.logo_path || '',
        color: tech.color || 'gray'
      })) || [],
      dnsRecords: subdomain.dnsRecords,
      tlsCertificate: subdomain.tlsCertificate,
      ipAddresses: subdomain.ipAddresses?.map((ip: any) => ip.toString()) || []
    }));
    return (
      <SubdomainsTable
        subdomains={transformedSubdomains}
        currentPage={currentPage}
        onPageChange={setCurrentPage}
        totalSubdomains={paginationInfo.count}
        itemsPerPage={10}
        paginationInfo={paginationInfo}
      />
    );
  };

  const renderIpAddressContent = () => {
    const transformedIpAddresses = parsedRelatedObjects.results.map((ip: any) => ({
      id: ip.id.toString(),
      ipAddress: ip.ipAddress,
      hostname: ip.hostname,
      isActive: ip.isActive || false,
      riskScore: (ip.riskScore || 'n/a').toUpperCase(),
      updatedAt: new Date(ip.updatedAt).toLocaleString(),
      createdAt: new Date(ip.createdAt).toLocaleString(),
      tags: ip.tags || [],
      technologies: ip.technologies?.map((tech: any) => ({
        name: tech.name || 'Unknown',
        version: tech.version || 'Unknown',
        logo_path: tech.logo_path || '',
        color: tech.color || 'gray'
      })) || [],
      location: {
        country: ip.country,
        city: ip.city,
        region: ip.region,
        postal: ip.postal,
        timezone: ip.timezone,
        coordinates: ip.loc
      },
      network: {
        org: ip.org,
        asn: ip.asn,
        cdn: ip.cdn,
        cdnName: ip.cdnName,
        waf: ip.waf,
        wafName: ip.wafName
      },
      openPorts: ip.ports?.map((port: any) => ({
        port: port.number,
        protocol: port.protocol,
        service: port.service,
        state: port.state
      })) || []
    }));
    return (
      <IpAddressesTable
        ipAddresses={transformedIpAddresses}
        currentPage={currentPage}
        onPageChange={setCurrentPage}
        totalIpAddresses={paginationInfo.count}
        itemsPerPage={10}
        paginationInfo={paginationInfo}
      />
    );
  };

  const renderPortContent = () => {
    return (
      <PortsTable
        ports={parsedRelatedObjects.results}
        currentPage={currentPage}
        onPageChange={setCurrentPage}
        totalPorts={paginationInfo.count}
        itemsPerPage={10}
        paginationInfo={paginationInfo}
      />
    );
  };

  const renderFindingContent = () => {
    return (
      <FindingsTable
        findings={parsedRelatedObjects.results}
        selectedFindings={selectedFindings}
        onSelectAll={handleSelectAll}
        onSelectFinding={handleSelectFinding}
        onAssignFinding={handleAssignFinding}
        onAction={handleFindingAction}
        currentPage={currentPage}
        onPageChange={setCurrentPage}
        totalFindings={paginationInfo.count}
        itemsPerPage={10}
        paginationInfo={paginationInfo}
        assignees={assignees}
      />
    );
  };

  const renderPublicLeakContent = () => {
    return (
      <PublicLeaksList
        leaks={parsedRelatedObjects.results}
        currentPage={currentPage}
        onPageChange={setCurrentPage}
        totalLeaks={paginationInfo.count}
        itemsPerPage={10}
        paginationInfo={paginationInfo}
        showHeader={false}
        showFilters={false}
      />
    );
  };

  const renderStealerLeakContent = () => {
    return (
      <StealerLeaksList
        leaks={parsedRelatedObjects.results}
        currentPage={currentPage}
        onPageChange={setCurrentPage}
        totalLeaks={paginationInfo.count}
        itemsPerPage={10}
        paginationInfo={paginationInfo}
        showHeader={false}
        showFilters={false}
      />
    );
  };

  const renderDataLeakContent = () => {
    return (
      <DataLeaksList
        leaks={parsedRelatedObjects.results}
        currentPage={currentPage}
        onPageChange={setCurrentPage}
        totalLeaks={paginationInfo.count}
        itemsPerPage={10}
        paginationInfo={paginationInfo}
        showHeader={false}
        showFilters={false}
      />
    );
  };

  const renderSecurityNotification = () => {
    return (
      <div className="space-y-6">
        <h2 className="text-2xl font-bold text-white">Security Alert</h2>
        <div className="p-4 bg-red-900/20 border border-red-800 rounded-lg">
          <h3 className="text-lg font-semibold text-red-400">Security Warning</h3>
          {/* <p className="mt-2 text-gray-300">
            {notification?.description || 'A security issue has been detected that requires your attention.'}
          </p> */}
        </div>
        <div className="mt-4">
          <h3 className="text-lg font-semibold text-white">Recommended Actions</h3>
          <ul className="mt-2 list-disc list-inside space-y-1 text-gray-300">
            <li>Review the security alert details</li>
            <li>Check affected systems or assets</li>
            <li>Implement recommended security measures</li>
            <li>Update your security policies if needed</li>
          </ul>
        </div>
      </div>
    );
  };

  const renderDataNotification = () => {
    return (
      <div className="space-y-6">
        <h2 className="text-2xl font-bold text-white">Data Alert</h2>
        <div className="p-4 bg-blue-900/20 border border-blue-800 rounded-lg">
          <h3 className="text-lg font-semibold text-blue-400">Data Issue</h3>
          {/* <p className="mt-2 text-gray-300">
            {notification?.description || 'A data issue has been detected that requires your attention.'}
          </p> */}
        </div>
      </div>
    );
  };

  const renderSystemNotification = () => {
    return (
      <div className="space-y-6">
        <h2 className="text-2xl font-bold text-white">System Alert</h2>
        <div className="p-4 bg-yellow-900/20 border border-yellow-800 rounded-lg">
          <h3 className="text-lg font-semibold text-yellow-400">System Issue</h3>
          {/* <p className="mt-2 text-gray-300">
            {notification?.description || 'A system issue has been detected that requires your attention.'}
          </p> */}
        </div>
      </div>
    );
  };

  const renderDefaultNotification = () => {
    return (
      <div className="space-y-6">
        <h2 className="text-2xl font-bold text-white">{notification?.message || 'Notification Details'}</h2>
        {/* <p className="text-gray-300">
          {notification?.description || 'No description available for this notification.'}
        </p> */}
      </div>
    );
  };

  // const getNotificationIcon = (notification: Notification) => {
  //   switch (notification.objectType) {
  //     case 'Domain': return Globe;
  //     case 'Subdomain': return Globe;
  //     case 'Ip_Address': return Globe;
  //     case 'Ports': return Globe;
  //     case "Finding": return Shield;
  //     case "Publicleak": return Key;
  //     case "Stealerleak": return Key;
  //     case "Dataleak": return HashIcon;
  //     default: return Bell;
  //   }
  // };

  // const getNotificationColor = (notification: Notification) => {
  //   switch (notification.severity) {
  //     case 'critical': return 'text-red-500 bg-red-500/10';
  //     case 'high': return 'text-red-500 bg-red-500/10';
  //     case 'info': return 'text-blue-500 bg-blue-500/10';
  //     case 'medium': return 'text-purple-500 bg-purple-500/10';
  //     case 'low': return 'text-emerald-500 bg-emerald-500/10';
  //     default: return 'text-gray-500 bg-gray-500/10';
  //   }
  // };

  const getSeverityColor = (severity: string) => {
    switch (severity) {
      case 'critical': return 'text-red-500 bg-red-500/10';
      case 'high': return 'text-orange-500 bg-orange-500/10';
      case 'medium': return 'text-yellow-500 bg-yellow-500/10';
      case 'low': return 'text-blue-500 bg-blue-500/10';
      default: return 'text-gray-500 bg-gray-500/10';
    }
  };

  // Render different content based on notification type
  const renderNotificationContent = () => {
    if (!notification) return null;

    // You can customize the rendering based on the notification type
    switch (notification.objectType) {
      case 'Domain':
        return renderDomainContent();
      case 'Subdomain':
        return renderSubdomainContent();
      case 'Ip_Address':
        return renderIpAddressContent();
      case 'Port':
        return renderPortContent();
      case 'Finding':
        if (findings !== parsedRelatedObjects.results) {
          setFindings(parsedRelatedObjects.results);
        }
        return renderFindingContent();
      case 'Publicleak':
        return renderPublicLeakContent();
      case 'Stealerleak':
        return renderStealerLeakContent();
      case 'Dataleak':
        return renderDataLeakContent();
      default:
        return renderDefaultNotification();
    }
  };

  // Render the sidebar with all notifications
  const renderNotificationsSidebar = () => {
    if (sidebarLoading) {
      return (
        <div className="animate-pulse space-y-4 p-4">
          {[...Array(5)].map((_, index) => (
            <div key={index} className="flex items-start space-x-3">
              <div className="rounded-lg bg-gray-700 h-10 w-10"></div>
              <div className="flex-1">
                <div className="h-4 bg-gray-700 rounded w-3/4 mb-2"></div>
                <div className="h-3 bg-gray-700 rounded w-1/2"></div>
              </div>
            </div>
          ))}
        </div>
      );
    }

    if (allNotifications.length === 0) {
      return (
        <div className="p-4 text-center text-gray-400">
          No notifications found
        </div>
      );
    }

    return (
      <div className="overflow-y-auto max-h-[calc(100vh-180px)]">
        {allNotifications.map((notif) => {
          const Icon = getNotificationIcon(notif);
          const colorClass = getNotificationColor(notif);
          const isSelected = notif.id.toString() === id;

          return (
            <div 
              key={notif.id}
              onClick={() => {
                setAllNotifications(prev => prev.map(n => n.id === notif.id ? { ...n, read: true } : n))
                handleReadNotification(notif.id, notif);
                navigate(`/notifications/${notif.objectType}/${notif.id}`)
              }}
              className={`p-3 border-b border-gray-800 cursor-pointer hover:bg-gray-800/50 transition-colors ${
                isSelected ? 'bg-gray-800/80' : ''
              }`}
            >
              <div className="flex items-start space-x-3">
                <div className={`p-2 rounded-lg ${colorClass} relative`}>
                  <Icon className="h-4 w-4" />
                  {!notif.read && (
                    <span className="absolute top-0 right-0 h-2 w-2 bg-red-500 rounded-full" />
                  )}
                </div>
                
                <div className="flex-1 min-w-0">
                  <h3 className={`text-sm font-medium ${isSelected ? 'text-white' : 'text-gray-300'} line-clamp-2`}>
                    {notif.message}
                  </h3>
                  
                  <div className="flex items-center justify-between mt-1">
                    <span className="text-xs text-gray-500">
                      {formatTimestamp(notif.timestamp)}
                    </span>
                    {notif.read && (
                      <span className="flex items-center text-emerald-500 text-xs">
                        <Check className="h-3 w-3 mr-1" />
                        Read
                      </span>
                    )}
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="max-w-4xl mx-auto p-6">
        <div className="bg-[#1A1F2E] rounded-lg border border-gray-800 p-6 text-center">
          <div className="animate-pulse">
            <div className="h-8 bg-gray-700 rounded w-1/3 mx-auto mb-4"></div>
            <div className="h-4 bg-gray-700 rounded w-1/2 mx-auto mb-2"></div>
            <div className="h-4 bg-gray-700 rounded w-2/3 mx-auto"></div>
          </div>
          <p className="mt-4 text-gray-400">Loading notification details...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-4xl mx-auto p-6">
        <div className="bg-[#1A1F2E] rounded-lg border border-gray-800 p-6 text-center">
          <div className="text-red-400 mb-4">
            <FileWarning className="h-12 w-12 mx-auto" />
          </div>
          <p className="text-gray-300">{error}</p>
          <button 
            onClick={() => fetchNotificationDetail()}
            className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  if (!notification) {
    return (
      <div className="max-w-4xl mx-auto p-6">
        <div className="bg-[#1A1F2E] rounded-lg border border-gray-800 p-6 text-center">
          <p className="text-gray-300">No notification found with the provided ID.</p>
        </div>
      </div>
    );
  }

  const Icon = getNotificationIcon(notification);
  const colorClass = getNotificationColor(notification);

  return (
    <div className="flex w-full">
      {/* Main content */}
      <div className="flex-1 max-w-[100%] 2xl:max-w-[100%] xl:max-w-[100%] lg:max-w-[100%] md:max-w-[100%]">
        {/* <Link to="/notifications" className="inline-flex items-center text-gray-400 hover:text-gray-300 mb-6">
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Notifications
        </Link> */}

        <div className="bg-[#1A1F2E] rounded-lg border border-gray-800 p-6">
          <div className="flex items-start space-x-4 mb-6">
            <div className={`p-3 rounded-lg ${colorClass}`}>
              <Icon className="h-6 w-6" />
            </div>
            
            <div className="flex-1">
              <h1 className="text-xl font-semibold text-white mb-2">
                {notification.message}
              </h1>
              
              <div className="flex items-center space-x-4 text-sm text-gray-400">
                <div className="flex items-center">
                  <Clock className="h-4 w-4 mr-2" />
                  {formatTimestamp(notification.timestamp)}
                </div>
                {(notification.read || true) && (
                  <div className="flex items-center text-emerald-500">
                    <Check className="h-4 w-4 mr-1" />
                    Read
                  </div>
                )}
              </div>
            </div>
          </div>

          {renderNotificationContent()}
        </div>
      </div>
      
      {/* Sidebar */}
      {/* <div className="ml-6 w-[30%] xl:w-[25%] lg:w-[30%] flex-shrink-0">
        <div className="bg-[#1A1F2E] rounded-lg border border-gray-800 sticky top-6">
          <div className="p-3 border-b border-gray-800">
            <h2 className="text-md font-semibold text-white flex items-center">
              <Bell className="h-4 w-4 mr-2 text-gray-400" />
              All Notifications
            </h2>
          </div>
          {renderNotificationsSidebar()}
        </div>
      </div> */}
    </div>
  );
};

export default GenericNotificationDetail;
